import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";
import { Globe, CheckCircle, XCircle, Hourglass, ClockCounterClockwise } from "@phosphor-icons/react";

const STATUS_META = {
  pending_approval: { label: "Pending approval", color: "bg-amber-50 text-amber-800 border-amber-200", Icon: Hourglass },
  pending_dns: { label: "Awaiting DNS", color: "bg-blue-50 text-blue-800 border-blue-200", Icon: ClockCounterClockwise },
  active: { label: "Active", color: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
};

export default function AdminCustomDomains() {
  const [items, setItems] = useState([]);
  const [targetHost, setTargetHost] = useState("");
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/custom-domains");
      setItems(data.items || []);
      setTargetHost(data.target_host || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (row) => {
    try {
      await api.post(`/admin/custom-domains/${row.id}/approve`);
      toast.success(`Approved — ${row.name} can now verify DNS.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Approve failed");
    }
  };

  const submitReject = async () => {
    if (!rejecting) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/custom-domains/${rejecting.id}/reject`, { reason: rejectReason });
      toast.success(`Rejected — ${rejecting.name}.`);
      setRejecting(null);
      setRejectReason("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reject failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl" data-testid="admin-domains-page">
      <Toaster richColors position="top-right" />
      <div className="mb-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">
          Platform · white-label
        </div>
        <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans" }}>
          Custom domains
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Approve or reject courier custom-domain requests. Once approved, the courier sets DNS and verifies themselves.
        </p>
        {targetHost && (
          <p className="text-xs text-zinc-400 mt-2">
            Couriers should CNAME their subdomain → <code className="font-mono text-zinc-700">{targetHost}</code>
          </p>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-zinc-200 rounded-md p-10 text-center" data-testid="admin-domains-empty">
          <div className="w-12 h-12 mx-auto rounded-md bg-zinc-100 text-zinc-500 flex items-center justify-center mb-3">
            <Globe size={22} weight="bold" />
          </div>
          <div className="font-medium text-zinc-700">No custom-domain requests yet</div>
          <div className="text-sm text-zinc-500 mt-1">
            Courier admins can request a domain from their Settings → Custom domain page.
          </div>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-md overflow-hidden" data-testid="admin-domains-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Company</th>
                <th className="text-left px-4 py-2 font-semibold">Domain</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold">Requested</th>
                <th className="text-right px-4 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const meta = STATUS_META[row.custom_domain_status] || STATUS_META.pending_approval;
                const StatusIcon = meta.Icon;
                const canApprove = ["pending_approval", "rejected"].includes(row.custom_domain_status);
                return (
                  <tr key={row.id} className="border-t border-zinc-100" data-testid={`domain-row-${row.id}`}>
                    <td className="px-4 py-3 font-medium text-zinc-900">{row.name}</td>
                    <td className="px-4 py-3">
                      <code className="font-mono text-zinc-800">{row.custom_domain}</code>
                      {row.custom_domain_status === "rejected" && row.custom_domain_rejected_reason && (
                        <div className="text-xs text-red-600 mt-1">{row.custom_domain_rejected_reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider border rounded-full px-2 py-0.5 ${meta.color}`}>
                        <StatusIcon size={10} weight="fill" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">
                      {row.custom_domain_requested_at ? new Date(row.custom_domain_requested_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {canApprove && (
                        <Button
                          size="sm"
                          onClick={() => approve(row)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white mr-1"
                          data-testid={`domain-approve-${row.id}`}
                        >
                          <CheckCircle size={13} className="mr-1.5" weight="bold" /> Approve
                        </Button>
                      )}
                      {row.custom_domain_status !== "rejected" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setRejecting(row); setRejectReason(""); }}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`domain-reject-${row.id}`}
                        >
                          <XCircle size={13} className="mr-1.5" weight="bold" /> Reject
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o) setRejecting(null); }}>
        <DialogContent className="max-w-md" data-testid="domain-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject {rejecting?.name}&apos;s domain</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="reject-reason" className="text-xs uppercase tracking-wider text-zinc-500">Reason (shown to the courier admin)</Label>
            <Input
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why this request is being rejected"
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRejecting(null)} disabled={submitting}>Cancel</Button>
            <Button
              onClick={submitReject}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-reject-btn"
            >
              {submitting ? "Rejecting…" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
